import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Check, X, MessageCircle, Users, Search, Trash2, Image, Video, Mic, Flag, MoreVertical, Play, Pause, Square, Phone, MapPin, CornerUpLeft, Smile, Languages, Download, User, Lock, Gift, FileText } from 'lucide-react';
import VoiceCallModal from '../components/VoiceCallModal';
import VoicePreviewBar from '../components/VoicePreviewBar';
import { useScreenshotDetection } from '../hooks/useScreenshotDetection';
import GroupsPanel from '../components/GroupsPanel';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import LocationMap from '../components/LocationMap';
import { formatDayLabel } from '../utils/dateLabel';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { getMediaUrl } from '../utils/mediaUrl';
import { Geolocation } from '@capacitor/geolocation';
import { downloadMedia } from '../utils/downloadMedia';
import VerifiedBadge from '../components/VerifiedBadge';
import { useLanguage } from '../context/LanguageContext';
import MediaLightbox from '../components/MediaLightbox';
import GiftPicker from '../components/GiftPicker';
import { renderMentions } from '../utils/mentions';
import StoryViewer from '../components/StoryViewer';

// Cached catalog so we don't fetch /api/gifts/catalog on every gift bubble.
let __giftCatalogCache = null;
let __giftCatalogPromise = null;
const useGiftCatalog = () => {
  const [c, setC] = useState(__giftCatalogCache);
  useEffect(() => {
    if (c) return;
    if (!__giftCatalogPromise) {
      __giftCatalogPromise = axios.get('/api/gifts/catalog').then(r => {
        __giftCatalogCache = r.data?.gifts || [];
        return __giftCatalogCache;
      }).catch(() => { __giftCatalogCache = []; return []; });
    }
    __giftCatalogPromise.then(setC);
  }, [c]);
  return c || [];
};

const GiftEmojiInline = ({ giftId }) => {
  const catalog = useGiftCatalog();
  const g = catalog.find(x => x.id === giftId);
  return <span aria-hidden>{g?.emoji || '🎁'}</span>;
};

// Voice Message Player Component - Styled for iOS with error handling
const VoiceMessagePlayer = ({ src, isOwn, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [blobSrc, setBlobSrc] = useState(null);
  const audioRef = useRef(null);
  const autoPlayedRef = useRef(false);
  // If the user taps play before the audio finished buffering, queue the play
  // here so 'canplay' can flush it. Removes the "two-tap" bug on iOS.
  const pendingPlayRef = useRef(false);

  // Prefetch audio file into a Blob URL so playback starts instantly on tap.
  // iOS Safari/Capacitor often refuses to autoload remote audio, forcing two
  // taps to start. With an in-memory blob, the first tap is instant.
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    const controller = new AbortController();
    fetch(src, { signal: controller.signal })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setBlobSrc(blobUrl);
      })
      .catch(() => { /* fall back to direct streaming via src */ });
    return () => {
      cancelled = true;
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      // iOS sometimes reports Infinity duration, wait for actual duration
      if (audio.duration && audio.duration !== Infinity) {
        setDuration(audio.duration);
        setIsLoaded(true);
        setIsLoading(false);
        setHasError(false);
      }
    };

    const handleDurationChange = () => {
      // iOS workaround: duration may change after initial load
      if (audio.duration && audio.duration !== Infinity && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoaded(true);
        setIsLoading(false);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // iOS fallback: get duration from timeupdate if not set
      if (!duration && audio.duration && audio.duration !== Infinity && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    const handleError = (e) => {
      console.error('Audio load error:', e, src);
      setHasError(true);
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setHasError(false);
      // Flush any pending play that was queued from a tap-before-ready.
      // This eliminates the "two-tap" bug on iOS for messages where the
      // user tapped before the audio finished buffering.
      if (pendingPlayRef.current && audioRef.current) {
        pendingPlayRef.current = false;
        const p = audioRef.current.play();
        if (p && typeof p.then === 'function') {
          p.then(() => setIsPlaying(true)).catch(() => {});
        } else {
          setIsPlaying(true);
        }
        return;
      }
      // Auto-play once when audio is ready (e.g. just-sent voice sticker).
      // Triggered as a result of the user's send-click gesture, satisfying iOS autoplay rules.
      if (autoPlay && !autoPlayedRef.current) {
        autoPlayedRef.current = true;
        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.then(() => setIsPlaying(true)).catch(() => {
            // Silently ignore autoplay failures (browser policy)
          });
        } else {
          setIsPlaying(true);
        }
      }
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadstart', handleLoadStart);

    // Load the audio
    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [src, duration, autoPlay]);

  const togglePlay = async (e) => {
    // Prevent the click from bubbling to the message bubble (which would trigger a "reply" selection).
    e?.stopPropagation?.();
    if (!audioRef.current || hasError) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        return;
      }
      // OPTIMISTIC UX — flip the icon to "playing" immediately so the user
      // gets feedback on the FIRST tap. This eliminates the "feels broken,
      // I tap again" two-tap bug on iOS for slow-buffering audio.
      setIsPlaying(true);

      // If audio is not yet ready (readyState < HAVE_CURRENT_DATA = 2),
      // queue a pending play and trigger load() — handleCanPlay will
      // start playback as soon as buffering allows.
      if (audioRef.current.readyState < 2) {
        pendingPlayRef.current = true;
        setIsLoading(true);
        try { audioRef.current.load(); } catch (_) {}
        return;
      }
      // iOS requires user gesture to play, this is called from button click
      audioRef.current.currentTime = currentTime || 0;
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => { /* already optimistically set */ })
          .catch((error) => {
            console.error('Play error:', error);
            // Queue for canplay flush instead of giving up
            pendingPlayRef.current = true;
            try { audioRef.current.load(); } catch (_) {}
          });
      }
    } catch (error) {
      console.error('Play error:', error);
      setIsPlaying(false);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Show error state
  if (hasError) {
    return (
      <div className={`mb-2 flex items-center gap-3 p-2 rounded-xl ${
        isOwn ? 'bg-white/10' : 'bg-black/20'
      }`}>
        <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${
          isOwn ? 'bg-red-500/20 text-red-300' : 'bg-red-500/20 text-red-400'
        }`}>
          <Mic className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs ${isOwn ? 'text-white/50' : 'text-gray-500'}`}>
            Audio non disponible
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`mb-2 flex items-center gap-3 p-2 rounded-xl ${
      isOwn ? 'bg-white/10' : 'bg-black/20'
    }`} onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} data-testid="voice-message-player">
      <audio ref={audioRef} src={blobSrc || src} preload="auto" />
      
      {/* Play/Pause Button */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={isLoading}
        data-testid="voice-play-btn"
        className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
          isOwn 
            ? 'bg-white/20 hover:bg-white/30 text-white' 
            : 'bg-magenta-500/20 hover:bg-magenta-500/30 text-magenta-400'
        } ${isLoading ? 'opacity-50' : ''}`}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-5 h-5" />
        ) : (
          <Play className="w-5 h-5 ml-0.5" />
        )}
      </button>

      {/* Waveform / Progress Bar */}
      <div className="flex-1 min-w-0">
        <div
          className="flex items-center gap-1 mb-1 cursor-pointer select-none py-1"
          data-testid="voice-message-seekbar"
          onClick={(e) => {
            e.stopPropagation();
            const a = audioRef.current; if (!a || !duration) return;
            const r = e.currentTarget.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
            a.currentTime = ratio * duration; setCurrentTime(a.currentTime);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            const a = audioRef.current; if (!a || !duration) return;
            const t = e.touches[0]; const r = e.currentTarget.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (t.clientX - r.left) / r.width));
            a.currentTime = ratio * duration; setCurrentTime(a.currentTime);
          }}
        >
          {/* Waveform bars (tap to seek) */}
          {[...Array(20)].map((_, i) => {
            const barHeight = Math.sin((i / 20) * Math.PI) * 0.7 + 0.3;
            const isActive = (i / 20) * 100 <= progress;
            return (
              <div
                key={i}
                className={`w-1 rounded-full transition-all ${
                  isActive 
                    ? (isOwn ? 'bg-white' : 'bg-magenta-400')
                    : (isOwn ? 'bg-white/30' : 'bg-gray-500')
                }`}
                style={{ height: `${barHeight * 20}px` }}
              />
            );
          })}
        </div>
        
        {/* Time + rewind / forward 10 s */}
        <div className={`flex items-center gap-2 text-xs ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
          <button onClick={(e) => { e.stopPropagation(); const a = audioRef.current; if (a) { a.currentTime = Math.max(0, a.currentTime - 10); setCurrentTime(a.currentTime); } }} className="px-1.5 py-0.5 rounded-full bg-black/20 hover:bg-black/30 text-[10px] font-semibold" aria-label="-10s" data-testid="voice-rewind-10">« 10</button>
          <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
          <button onClick={(e) => { e.stopPropagation(); const a = audioRef.current; if (a) { a.currentTime = Math.min(duration || a.duration || 0, a.currentTime + 10); setCurrentTime(a.currentTime); } }} className="px-1.5 py-0.5 rounded-full bg-black/20 hover:bg-black/30 text-[10px] font-semibold" aria-label="+10s" data-testid="voice-forward-10">10 »</button>
        </div>
      </div>

      {/* Mic icon */}
      <Mic className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/50' : 'text-gray-500'}`} />
    </div>
  );
};

// Media Image Component with error handling
const MediaImage = ({ src, isOwn, onOpen }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  if (hasError) {
    return (
      <div className={`mb-2 flex items-center justify-center rounded-lg p-4 ${
        isOwn ? 'bg-white/10' : 'bg-black/20'
      }`} style={{ minHeight: '100px', minWidth: '150px' }}>
        <div className="text-center">
          <Image className={`w-8 h-8 mx-auto mb-1 ${isOwn ? 'text-white/30' : 'text-gray-500'}`} />
          <p className={`text-xs ${isOwn ? 'text-white/50' : 'text-gray-500'}`}>Image non disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 relative group">
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center rounded-lg ${
          isOwn ? 'bg-white/10' : 'bg-black/20'
        }`} style={{ minHeight: '100px', minWidth: '150px' }}>
          <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin text-white/50" />
        </div>
      )}
      <img
        src={src}
        alt="Image"
        className={`max-w-full max-h-64 rounded-lg object-contain cursor-zoom-in ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onClick={(e) => { e.stopPropagation(); if (!isLoading && !hasError) onOpen?.(src); }}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
        data-testid="chat-image-thumb"
      />
      {!isLoading && !hasError && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await downloadMedia({ url: src, filename: `destine-image-${Date.now()}` });
              toast.success('Image téléchargée');
            } catch (err) {
              console.error(err);
              toast.error('Téléchargement impossible');
            }
          }}
          className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 sm:opacity-90"
          data-testid="download-chat-image-btn"
          aria-label="Télécharger l'image"
          title="Télécharger l'image"
        >
          <Download className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Media Video Component with error handling
const MediaVideo = ({ src, isOwn, onOpen }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      setIsLoading(false);
      setHasError(false);
    };

    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [src]);

  if (hasError) {
    return (
      <div className={`mb-2 flex items-center justify-center rounded-lg p-4 ${
        isOwn ? 'bg-white/10' : 'bg-black/20'
      }`} style={{ minHeight: '100px', minWidth: '150px' }}>
        <div className="text-center">
          <Video className={`w-8 h-8 mx-auto mb-1 ${isOwn ? 'text-white/30' : 'text-gray-500'}`} />
          <p className={`text-xs ${isOwn ? 'text-white/50' : 'text-gray-500'}`}>Vidéo non disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 relative group">
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center rounded-lg ${
          isOwn ? 'bg-white/10' : 'bg-black/20'
        }`} style={{ minHeight: '100px', minWidth: '150px' }}>
          <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin text-white/50" />
        </div>
      )}
      <video
        ref={videoRef}
        src={src}
        controls
        className={`max-w-full max-h-64 rounded-lg cursor-zoom-in ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        playsInline
        preload="metadata"
        onClick={(e) => {
          // Tap on the video preview (not on controls) opens the lightbox.
          // We detect "controls click" by checking offsetY > height-40 (the bottom control bar).
          const rect = e.currentTarget.getBoundingClientRect();
          if (e.clientY - rect.top < rect.height - 50 && !isLoading && !hasError) {
            e.preventDefault();
            e.stopPropagation();
            videoRef.current?.pause();
            onOpen?.(src);
          }
        }}
        data-testid="chat-video-thumb"
      />
      {!isLoading && !hasError && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await downloadMedia({ url: src, filename: `destine-video-${Date.now()}` });
              toast.success('Vidéo téléchargée');
            } catch (err) {
              console.error(err);
              toast.error('Téléchargement impossible');
            }
          }}
          className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-all opacity-90"
          data-testid="download-chat-video-btn"
          aria-label="Télécharger la vidéo"
          title="Télécharger la vidéo"
        >
          <Download className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const Messages = () => {
  const { conversationId, receiverId } = useParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [groupInvitations, setGroupInvitations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [view, setView] = useState('conversations'); // conversations, invitations, chat
  const [lightbox, setLightbox] = useState(null); // {type, src} | null
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // v47.9: typing indicators (1-to-1 only) + emoji reactions
  const [typingUsers, setTypingUsers] = useState([]); // [{user, updated_at}]
  const typingDebounceRef = useRef(null);
  const lastTypingPingRef = useRef(0);
  const [reactionPickerFor, setReactionPickerFor] = useState(null); // message id
  const longPressTimerRef = useRef(null);
  const [showGiftPicker, setShowGiftPicker] = useState(false);

  const sendGift = async (gift) => {
    const conv = activeConversation;
    if (!conv?.conversation_id) return;
    const isGroup = !!conv.is_group;
    try {
      const res = await axios.post('/api/gifts/send', {
        gift_id: gift.id,
        target_type: isGroup ? 'group' : 'conversation',
        target_id: isGroup ? (conv.group_id || conv.id) : conv.conversation_id,
      });
      setShowGiftPicker(false);
      if (res.data?.message) {
        setMessages(prev => [...prev, res.data.message]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cadeau impossible');
    }
  };

  // Notify backend that I'm typing — throttle to once every 3s
  const pingTyping = async () => {
    const conv = activeConversation;
    if (!conv?.conversation_id || conv?.isNew || conv?.is_group) return;
    const now = Date.now();
    if (now - lastTypingPingRef.current < 1500) return;
    lastTypingPingRef.current = now;
    try { await axios.post(`/api/messages/conversations/${conv.conversation_id}/typing`); } catch (_) {}
  };

  const stopTyping = (convId) => {
    if (!convId) return;
    lastTypingPingRef.current = 0;
    try { axios.delete(`/api/messages/conversations/${convId}/typing`); } catch (_) {}
  };

  // Poll who's typing every 1s while in a 1-to-1 chat (near-instant), paused when app is in background
  useEffect(() => {
    const conv = activeConversation;
    if (!conv?.conversation_id || conv?.isNew || conv?.is_group) {
      setTypingUsers([]);
      return;
    }
    let alive = true;
    let inFlight = false;
    const tick = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const res = await axios.get(`/api/messages/conversations/${conv.conversation_id}/typing`);
        if (alive) setTypingUsers(res.data?.typing || []);
      } catch (_) { /* silent */ } finally { inFlight = false; }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(id); };
  }, [activeConversation?.conversation_id, activeConversation?.is_group, activeConversation?.isNew]);

  // Send "stop typing" when leaving the conversation or clearing the input
  useEffect(() => {
    const conv = activeConversation;
    if (!conv?.conversation_id || conv?.isNew || conv?.is_group) return;
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    if (newMessage.trim().length > 0) {
      pingTyping();
      typingDebounceRef.current = setTimeout(() => stopTyping(conv.conversation_id), 3000);
    } else {
      stopTyping(conv.conversation_id);
    }
    return () => { if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current); };
    // eslint-disable-next-line
  }, [newMessage, activeConversation?.conversation_id]);

  // Toggle a reaction on a message
  const reactToMessage = async (msg, emoji) => {
    setReactionPickerFor(null);
    try {
      const scope = activeConversation?.is_group ? 'group' : 'direct';
      const body = { emoji, scope };
      if (scope === 'group') body.group_id = activeConversation.group_id || activeConversation.id;
      const res = await axios.post(`/api/messages/${msg.id}/react`, body);
      // Optimistic local update
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: res.data.reactions } : m));
    } catch (_) {
      toast.error('Réaction impossible');
    }
  };

  // Long-press handler (works for both mouse + touch)
  const handleMessageLongPressStart = (msg) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setReactionPickerFor(msg.id);
      // Haptic feedback on native
      try { if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(30); } catch (_) {}
    }, 450);
  };
  const handleMessageLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const [searching, setSearching] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [reportingMessage, setReportingMessage] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [pendingVoice, setPendingVoice] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [otherUserPhone, setOtherUserPhone] = useState(null);
  const [storyViewerUserId, setStoryViewerUserId] = useState(null);
  const [otherUserEsimNumbers, setOtherUserEsimNumbers] = useState([]);
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  // 'audio' or 'video' — controls which capabilities VoiceCallModal initialises.
  const [callType, setCallType] = useState('audio');
  const isNativeMobile = Capacitor.isNativePlatform();

  // Activate iOS screenshot detection while on an active conversation
  useScreenshotDetection(activeConversation?.conversation_id);

  const handleVoiceCallClick = async () => {
    const peerId = activeConversation?.other_user?.id;
    if (!peerId) return;
    // Check DND just-in-time so a stale or failed fetch never blocks the button
    try {
      const res = await axios.get(`/api/privacy/dnd/${peerId}`);
      if (res.data?.dnd_enabled) {
        toast.info(t('calls.peer_dnd') || 'Cet utilisateur a activé Ne pas déranger');
        return;
      }
    } catch (e) { /* allow call if check fails */ }
    setCallType('audio');
    setShowVoiceCall(true);
  };

  const handleVideoCallClick = async () => {
    const peerId = activeConversation?.other_user?.id;
    if (!peerId) return;
    try {
      const res = await axios.get(`/api/privacy/dnd/${peerId}`);
      if (res.data?.dnd_enabled) {
        toast.info(t('calls.peer_dnd') || 'Cet utilisateur a activé Ne pas déranger');
        return;
      }
    } catch (e) { /* allow call if check fails */ }
    setCallType('video');
    setShowVoiceCall(true);
  };
  const [sendingLocation, setSendingLocation] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [stickers, setStickers] = useState([]);
  const [sendingSticker, setSendingSticker] = useState(null);
  const [translatingMsgId, setTranslatingMsgId] = useState(null);
  const [translationResult, setTranslationResult] = useState(null);
  const [translateTargetMsg, setTranslateTargetMsg] = useState(null); // {msg} → opens lang picker
  // True while the user is inside a group chat (managed by GroupsPanel) — used
  // to hide the tabs / bottom Navigation for a true full-screen chat.
  const [inGroupChat, setInGroupChat] = useState(false);
  // Track message ids that should auto-play once on first render (e.g. just-sent voice stickers).
  const autoPlayMessageIdsRef = useRef(new Set());

  const SUPPORTED_TRANSLATIONS = [
    { code: 'fr', label: '🇫🇷 Français' },
    { code: 'en', label: '🇬🇧 Anglais' },
    { code: 'es', label: '🇪🇸 Espagnol' },
    { code: 'pt', label: '🇵🇹 Portugais' },
    { code: 'de', label: '🇩🇪 Allemand' },
    { code: 'ar', label: '🇸🇦 Arabe' },
    { code: 'zh', label: '🇨🇳 Chinois' },
    { code: 'ja', label: '🇯🇵 Japonais' },
    { code: 'hi', label: '🇮🇳 Hindi' },
    { code: 'ru', label: '🇷🇺 Russe' },
    { code: 'nl', label: '🇳🇱 Néerlandais' },
    { code: 'sw', label: '🇰🇪 Swahili' },
    { code: 'ht', label: '🇭🇹 Kreyòl ayisyen' },
  ];

  const handleSendSticker = async (stickerId) => {
    if (!activeConversation?.conversation_id || activeConversation?.isNew) return;
    setSendingSticker(stickerId);
    try {
      const fd = new FormData();
      fd.append('conversation_id', activeConversation.conversation_id);
      fd.append('receiver_id', activeConversation.other_user?.id);
      const resp = await axios.post(`/api/voice-stickers/${stickerId}/send`, fd);
      if (resp.data) {
        // Mark this freshly-sent sticker for one-shot auto-play.
        if (resp.data.id) {
          autoPlayMessageIdsRef.current.add(resp.data.id);
        }
        setMessages(prev => [...prev, resp.data]);
        setShowStickerPicker(false);
      }
    } catch (e) {
      toast.error("Impossible d'envoyer le sticker");
    } finally {
      setSendingSticker(null);
    }
  };

  const handleTranslateVoice = async (msg, targetLang) => {
    if (!msg.media_url || msg.media_type !== 'voice') return;
    if (!targetLang) {
      // Dismiss the on-screen keyboard so the language picker is not hidden behind it.
      // On iOS Capacitor, blur() alone is NOT enough — the native keyboard stays
      // open unless we explicitly call Keyboard.hide(). We also wait a tick so
      // the keyboard animation completes before showing the modal.
      try { document.activeElement?.blur?.(); } catch (_) {}
      try {
        if (Capacitor.isNativePlatform()) {
          await Keyboard.hide();
        }
      } catch (_) {}
      // Small delay so iOS keyboardWillHide event fires and keyboardHeight
      // resets to 0 before the modal mounts (otherwise the modal is positioned
      // relative to the still-open keyboard).
      setTimeout(() => setTranslateTargetMsg(msg), 120);
      return;
    }
    setTranslateTargetMsg(null);
    setTranslatingMsgId(msg.id);
    try {
      const fd = new FormData();
      fd.append('audio_url', msg.media_url);
      fd.append('target_lang', targetLang);
      const resp = await axios.post('/api/translate-voice-by-url', fd);
      setTranslationResult({ ...resp.data, message_id: msg.id });
    } catch (e) {
      toast.error('Erreur de traduction');
    } finally {
      setTranslatingMsgId(null);
    }
  };

  useEffect(() => {
    if (showStickerPicker && stickers.length === 0) {
      axios.get('/api/voice-stickers')
        .then(res => setStickers(res.data?.stickers || []))
        .catch(() => {});
    }
  }, [showStickerPicker, stickers.length]);

  const handleShareLocation = async () => {
    if (!activeConversation?.conversation_id || sendingLocation) return;
    setSendingLocation(true);
    try {
      // Use Capacitor Geolocation plugin (works reliably on native iOS/Android)
      let latitude, longitude;
      if (Capacitor.isNativePlatform()) {
        const permStatus = await Geolocation.checkPermissions();
        if (permStatus.location === 'denied') {
          const reqResult = await Geolocation.requestPermissions();
          if (reqResult.location === 'denied') {
            toast.error('Accès à la localisation refusé. Activez dans Réglages > Destine.');
            return;
          }
        }
        const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } else {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          });
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      }

      const receiverUserId = activeConversation?.other_user?.id;
      await axios.post('/api/messages/location', {
        conversation_id: activeConversation.conversation_id,
        receiver_id: receiverUserId,
        latitude,
        longitude
      });
      toast.success('Position partagée (30 min)');
      loadConversation(activeConversation.conversation_id);
    } catch (error) {
      console.error('Location share error:', error);
      // Distinguish between geolocation errors and API errors
      if (error?.code === 1 || error?.message?.includes('denied')) {
        toast.error('Accès à la localisation refusé');
      } else if (error?.code === 2 || error?.message?.includes('unavailable')) {
        toast.error('Position non disponible');
      } else if (error?.code === 3 || error?.message?.includes('timeout')) {
        toast.error('Délai dépassé — réessayez');
      } else if (error?.response) {
        toast.error(error.response.data?.detail || 'Erreur serveur');
      } else {
        toast.error('Erreur de localisation — vérifiez vos paramètres');
      }
    } finally {
      setSendingLocation(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle iOS keyboard
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Use Capacitor Keyboard plugin for native apps
      const showListener = Keyboard.addListener('keyboardWillShow', (info) => {
        console.log('Keyboard will show, height:', info.keyboardHeight);
        setKeyboardHeight(info.keyboardHeight);
        // Scroll to bottom when keyboard shows
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      });

      const hideListener = Keyboard.addListener('keyboardWillHide', () => {
        console.log('Keyboard will hide');
        setKeyboardHeight(0);
      });

      return () => {
        showListener.then(l => l.remove());
        hideListener.then(l => l.remove());
      };
    } else {
      // Fallback for web using visualViewport API
      const handleResize = () => {
        if (window.visualViewport) {
          const viewportHeight = window.visualViewport.height;
          const windowHeight = window.innerHeight;
          const keyboardH = windowHeight - viewportHeight;
          if (keyboardH > 100) {
            setKeyboardHeight(keyboardH);
          } else {
            setKeyboardHeight(0);
          }
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
        return () => {
          window.visualViewport.removeEventListener('resize', handleResize);
        };
      }
    }
  }, []);

  useEffect(() => {
    if (receiverId) {
      // Start new conversation
      startNewConversation(receiverId);
    } else if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId, receiverId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [convResp, invResp, groupInvResp] = await Promise.all([
        axios.get('/api/messages/conversations'),
        axios.get('/api/messages/invitations/pending'),
        axios.get('/api/groups/invitations').catch(() => ({ data: [] })),
      ]);
      setConversations(Array.isArray(convResp.data) ? convResp.data : []);
      setInvitations(Array.isArray(invResp.data) ? invResp.data : []);
      setGroupInvitations(Array.isArray(groupInvResp.data) ? groupInvResp.data : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur de chargement');
      setConversations([]);
      setInvitations([]);
      setGroupInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupInvitation = async (req, accept) => {
    try {
      const endpoint = accept
        ? `/api/groups/invitations/${req.id}/accept`
        : `/api/groups/invitations/${req.id}/reject`;
      await axios.post(endpoint);
      toast.success(accept ? '✓' : '×');
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const startNewConversation = async (userId) => {
    setView('chat');
    
    // Get user info
    try {
      const userResp = await axios.get(`/api/users/${userId}`);
      setActiveConversation({ other_user: userResp.data, isNew: true });
      setMessages([]);
    } catch (error) {
      console.error('Error loading user:', error);
      setActiveConversation({ other_user: { id: userId }, isNew: true });
      setMessages([]);
    }
  };

  const loadConversation = async (convId) => {
    setView('chat');
    try {
      const messagesResp = await axios.get(`/api/messages/conversations/${convId}`);
      // Ensure response.data is always an array
      setMessages(Array.isArray(messagesResp.data) ? messagesResp.data : []);
      
      const conv = Array.isArray(conversations) ? conversations.find(c => c.conversation_id === convId) : null;
      if (conv) {
        setActiveConversation(conv);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast.error('Erreur de chargement');
      setMessages([]); // Set to empty array on error
    }
  };

  // Live-poll messages while a DM conversation is open (3 s).
  // Merges new messages without flashing the list.
  useEffect(() => {
    const conv = activeConversation;
    if (!conv?.conversation_id || conv?.isNew || conv?.is_group || view !== 'chat') return;
    let alive = true;
    const tick = async () => {
      try {
        const res = await axios.get(`/api/messages/conversations/${conv.conversation_id}`);
        if (!alive) return;
        const fresh = Array.isArray(res.data) ? res.data : [];
        setMessages(prev => {
          if (!Array.isArray(prev)) return fresh;
          if (fresh.length === prev.length) {
            // same length but check for content changes (e.g. read receipts, reactions)
            const lastA = prev[prev.length - 1]; const lastB = fresh[fresh.length - 1];
            if (lastA?.id === lastB?.id && JSON.stringify(lastA?.reactions) === JSON.stringify(lastB?.reactions)) return prev;
          }
          return fresh;
        });
      } catch (_) { /* silent */ }
    };
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [activeConversation?.conversation_id, activeConversation?.is_group, activeConversation?.isNew, view]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    // Hard limit: 1 Word page (3500 chars) for DMs
    if (newMessage.length > 3500) {
      toast.error(`Message trop long (${newMessage.length}/3500 caractères max — équivalent 1 page Word)`);
      return;
    }

    setSending(true);
    try {
      const receiverUserId = activeConversation?.other_user?.id || receiverId;
      
      await axios.post('/api/messages/send', null, {
        params: {
          receiver_id: receiverUserId,
          content: newMessage.trim(),
          ...(replyToMessage?.id ? { reply_to_id: replyToMessage.id } : {})
        }
      });
      
      setNewMessage('');
      setReplyToMessage(null);
      if (!activeConversation?.is_group) stopTyping(activeConversation?.conversation_id);
      // reset textarea height after sending
      if (inputRef.current) inputRef.current.style.height = 'auto';
      
      if (activeConversation?.isNew) {
        toast.success('Invitation envoyée !');
        // Redirect to the Discussions tab after the first invitation to a
        // brand-new contact (isNew=true is only set by the "New message" flow,
        // i.e. once per recipient — exactly the behaviour the user asked for).
        setTimeout(() => {
          setActiveConversation(null);
          setView('conversations');
          navigate('/messages', { replace: true });
          loadData();
        }, 600);
      } else {
        // Reload messages
        if (activeConversation?.conversation_id) {
          loadConversation(activeConversation.conversation_id);
        }
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleAcceptInvitation = async (invitationConvId) => {
    try {
      await axios.post(`/api/messages/invitations/${invitationConvId}/accept`);
      toast.success('Invitation acceptée');
      loadData();
    } catch (error) {
      console.error('Accept error:', error);
      toast.error('Erreur');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`/api/messages/${messageId}`);
      toast.success('Message supprimé');
      // Reload messages
      if (activeConversation?.conversation_id) {
        loadConversation(activeConversation.conversation_id);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de suppression');
    }
  };

  const handleRejectInvitation = async (invitationConvId) => {
    try {
      await axios.post(`/api/messages/invitations/${invitationConvId}/reject`);
      toast.success('Invitation rejetée');
      loadData();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Erreur');
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeConversation?.conversation_id) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      await axios.delete(`/api/conversations/${activeConversation.conversation_id}`);
      toast.success('Conversation supprimée');
      setView('conversations');
      setActiveConversation(null);
      loadData();
    } catch (error) {
      console.error('Delete conversation error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReportMessage = async () => {
    if (!reportReason.trim()) {
      toast.error('Veuillez indiquer la raison du signalement');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('message_id', reportingMessage);
      formData.append('reason', reportReason);
      
      await axios.post('/api/reports/message', formData);
      toast.success('Signalement envoyé');
      setReportingMessage(null);
      setReportReason('');
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Erreur lors du signalement');
    }
  };

  const handleFileSelect = async (event, mediaType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // PDFs: cap at 30 MB (matches backend report_routes.py)
    if (mediaType === 'pdf') {
      if (file.size > 30 * 1024 * 1024) {
        toast.error('PDF trop volumineux (max 30 Mo)');
        return;
      }
      await uploadMedia(file, 'pdf');
      setShowMediaOptions(false);
      return;
    }

    // Check file size (60MB max for image/video/voice)
    if (file.size > 60 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 60MB)');
      return;
    }
    
    // Check video duration if it's a video
    if (mediaType === 'video') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = async function() {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > 120) {
          toast.error('La vidéo doit faire moins de 2 minutes');
          return;
        }
        await uploadMedia(file, mediaType);
      };
      video.src = URL.createObjectURL(file);
    } else {
      await uploadMedia(file, mediaType);
    }
    
    setShowMediaOptions(false);
  };

  const uploadMedia = async (file, mediaType) => {
    try {
      setSending(true);
      const formData = new FormData();
      formData.append('conversation_id', activeConversation.conversation_id);
      formData.append('receiver_id', activeConversation.other_user?.id);
      formData.append('media_type', mediaType);
      formData.append('media', file);
      
      const response = await axios.post('/api/messages/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Add message to local state immediately for instant feedback
      if (response.data) {
        setMessages(prev => [...prev, response.data]);
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      
      toast.success('Média envoyé');
      
      // Also reload to ensure consistency
      setTimeout(() => {
        loadConversation(activeConversation.conversation_id);
      }, 500);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine best supported format (iOS uses mp4, Chrome uses webm)
      let mimeType = '';
      const formats = ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
      for (const format of formats) {
        if (MediaRecorder.isTypeSupported(format)) {
          mimeType = format;
          break;
        }
      }
      console.log('Voice recording format:', mimeType || 'default');
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      // Determine file extension
      const getExtension = (mime) => {
        if (mime.includes('mp4') || mime.includes('aac')) return 'mp4';
        if (mime.includes('ogg')) return 'ogg';
        return 'webm';
      };
      const fileExtension = getExtension(mimeType);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const actualMimeType = mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        stream.getTracks().forEach(track => track.stop());
        
        console.log('Voice recorded:', audioBlob.size, 'bytes, type:', actualMimeType);
        
        // Check duration
        if (recordingTime > 120) {
          toast.error('Le vocal doit faire moins de 2 minutes');
          return;
        }
        
        // Preview before sending (WhatsApp-style) — the user listens, then sends or discards.
        const file = new File([audioBlob], `vocal_${Date.now()}.${fileExtension}`, { type: actualMimeType });
        setPendingVoice({ file, url: URL.createObjectURL(audioBlob) });
      };
      
      mediaRecorder.start(100); // Collect data every 100ms for better reliability
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 120) {
            stopRecording();
            return 120;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`/api/search/users?query=${encodeURIComponent(query)}`);
      // Ensure response.data is always an array
      setSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur de recherche');
      setSearchResults([]); // Set to empty array on error
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen text-white flex items-center justify-center ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-magenta-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Chat view
  if (view === 'chat' && activeConversation) {
    return (
      <div className={`min-h-screen text-white flex flex-col ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        {/* Header */}
        <div className={`sticky top-0 z-40 backdrop-blur-md border-b border-white/10 p-4 ${isDark ? 'bg-black/90' : 'bg-white/10'}`}>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setView('conversations');
                setActiveConversation(null);
                navigate('/messages');
              }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h2 className="text-xl font-bold flex items-center gap-1.5">
                {activeConversation.other_user?.name || 'Utilisateur'}
                {activeConversation.other_user?.is_verified && <VerifiedBadge size="md" />}
              </h2>
              <p className="text-sm text-gray-400 flex items-center gap-1.5" data-testid="conv-header-status">
                @{activeConversation.other_user?.username}
                {typingUsers.length > 0 ? (
                  <span className="inline-flex items-center gap-1 text-magenta-300 text-xs italic">
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-magenta-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-magenta-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-magenta-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    écrit…
                  </span>
                ) : activeConversation.other_user?.is_online ? (
                  <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    En ligne
                  </span>
                ) : null}
              </p>
            </div>
            
            {/* Phone Call Button */}
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`/api/users/${activeConversation.other_user?.id}/phone-for-chat`);
                  const personal = res.data?.phone_number || null;
                  const esims = res.data?.esim_numbers || [];
                  if (personal || esims.length > 0) {
                    setOtherUserPhone(personal);
                    setOtherUserEsimNumbers(esims);
                    setShowPhoneModal(true);
                  } else {
                    toast.info('Cet utilisateur n\'a pas activé son numéro de téléphone');
                  }
                } catch (e) {
                  toast.info('Numéro non disponible');
                }
              }}
              className="p-2 hover:bg-green-500/20 rounded-full transition-colors"
              data-testid="phone-call-btn"
              title="Appeler"
            >
              <Phone className="w-5 h-5 text-green-500" />
            </button>

            {/* Voice Call Button (Agora) — mobile only */}
            {isNativeMobile && (
              <button
                onClick={handleVoiceCallClick}
                className="p-2 rounded-full transition-colors hover:bg-violet-500/20"
                data-testid="voice-call-btn"
                title="Appel vocal"
              >
                <Phone className="w-5 h-5 text-violet-400" />
              </button>
            )}

            {/* Video Call Button (Agora) — mobile only */}
            {isNativeMobile && (
              <button
                onClick={handleVideoCallClick}
                className="p-2 rounded-full transition-colors hover:bg-violet-500/20"
                data-testid="video-call-btn"
                title="Appel vidéo"
              >
                <Video className="w-5 h-5 text-violet-400" />
              </button>
            )}
            
            {/* Conversation Menu */}
            <div className="relative">
              <button
                onClick={() => setShowConversationMenu(!showConversationMenu)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              
              {showConversationMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-gray-900 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <button
                    onClick={() => {
                      setShowConversationMenu(false);
                      const u = activeConversation?.other_user;
                      if (u?.username) navigate(`/profile/u/${u.username}`);
                      else if (u?.id) navigate(`/profile/${u.id}`);
                    }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center space-x-2"
                    data-testid="conv-view-profile-btn"
                  >
                    <User className="w-4 h-4" />
                    <span>Voir le profil</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowConversationMenu(false);
                      handleDeleteConversation();
                    }}
                    className="w-full px-4 py-3 text-left text-red-400 hover:bg-red-500/10 flex items-center space-x-2"
                    data-testid="conv-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Supprimer la discussion</span>
                  </button>
                  <div className="px-4 py-2.5 text-[10px] text-gray-500 border-t border-white/5 flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    <span>Stockage chiffré</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 pb-24" data-testid="messages-list">
          {(!Array.isArray(messages) || messages.length === 0) && !activeConversation.isNew ? (
            <div className="text-center text-gray-400 py-10">
              {t('messages.noMessages')}
            </div>
          ) : (
            Array.isArray(messages) && messages.map((msg, index) => {
              const isOwn = msg.sender_id === user.id;
              const prevMsg = messages[index - 1];
              const showAvatarSpace = !prevMsg || prevMsg.sender_id !== msg.sender_id;

              // Date separator (WhatsApp-style) — show the day label whenever the
              // current message starts a new calendar day.
              const showDateSeparator =
                !prevMsg ||
                new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();
              const dateLabel = showDateSeparator ? formatDayLabel(msg.created_at) : null;

              // System message: screenshot detected
              if (msg.type === 'system_screenshot' || msg.system_event === 'screenshot_taken') {
                const text = isOwn
                  ? (t('screenshot.i_took') || 'Vous avez fait une capture d\'écran de cette discussion')
                  : (t('screenshot.taken') || '{name} a fait une capture d\'écran de cette discussion').replace('{name}', activeConversation?.other_user?.name || activeConversation?.other_user?.username || '');
                return (
                  <div key={msg.id || index} className="flex justify-center my-3" data-testid="message-system-screenshot">
                    <div className="bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 9l3-3h12l3 3v9a3 3 0 01-3 3H6a3 3 0 01-3-3V9zM12 11a3 3 0 100 6 3 3 0 000-6z"/></svg>
                      <span>{text}</span>
                    </div>
                  </div>
                );
              }

              return (
                <React.Fragment key={msg.id || index}>
                  {dateLabel && (
                    <div className="flex justify-center my-3" data-testid="date-separator">
                      <span className="text-[11px] font-medium text-white/60 bg-white/10 px-3 py-1 rounded-full">
                        {dateLabel}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group ${showAvatarSpace ? 'mt-3' : 'mt-0.5'}`}
                    data-testid={`message-${isOwn ? 'sent' : 'received'}`}
                  >
                  <div className={`flex items-end gap-1.5 max-w-[82%] ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Action button on hover/long-press */}
                    <button
                      onClick={() => {
                        if (isOwn) {
                          handleDeleteMessage(msg.id);
                        } else {
                          setReplyToMessage(msg);
                          inputRef.current?.focus();
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setReplyToMessage(msg);
                        inputRef.current?.focus();
                      }}
                      className="opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded-full self-center"
                      title={isOwn ? 'Supprimer' : 'Répondre'}
                      data-testid={isOwn ? 'delete-msg-btn' : 'reply-msg-btn'}
                    >
                      {isOwn ? <Trash2 className="w-3.5 h-3.5 text-red-400" /> : <CornerUpLeft className="w-3.5 h-3.5 text-gray-400" />}
                    </button>

                    {/* Quick-reply tap target on bubble itself (mobile-friendly) */}
                    <div
                      onClick={() => {
                        if (!isOwn) {
                          setReplyToMessage(msg);
                          inputRef.current?.focus();
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (!isOwn) {
                          setReplyToMessage(msg);
                          inputRef.current?.focus();
                        } else {
                          setReportingMessage(msg.id);
                        }
                      }}
                      onMouseDown={() => handleMessageLongPressStart(msg)}
                      onMouseUp={handleMessageLongPressEnd}
                      onMouseLeave={handleMessageLongPressEnd}
                      onTouchStart={() => handleMessageLongPressStart(msg)}
                      onTouchEnd={handleMessageLongPressEnd}
                      onTouchCancel={handleMessageLongPressEnd}
                      {...(isOwn && msg.media_type !== 'location' ? { 'data-keep-dark': 'true' } : {})}
                      className={`relative ${
                        msg.media_type === 'location'
                          ? 'p-0 overflow-hidden bg-transparent rounded-2xl'
                          : isOwn
                            ? 'px-3.5 py-2 bg-gradient-to-br from-magenta-500 to-violet-600 text-white rounded-2xl rounded-br-md shadow-md'
                            : `px-3.5 py-2 ${isDark ? 'bg-white/10' : 'bg-white/15'} text-white rounded-2xl rounded-bl-md shadow-md`
                      }`}
                    >
                      {/* Reply preview */}
                      {msg.reply_to && (
                        <div className={`mb-1.5 px-2 py-1 rounded-lg border-l-2 ${isOwn ? 'border-white/60 bg-white/15' : 'border-magenta-400 bg-black/25'}`}>
                          <p className="text-[10px] font-semibold opacity-80 mb-0.5">
                            {msg.reply_to.sender_id === user.id ? 'Vous' : (activeConversation.other_user?.name || '...')}
                          </p>
                          <p className="text-xs opacity-75 line-clamp-2">
                            {msg.reply_to.media_type === 'voice' ? '🎙️ Message vocal' :
                             msg.reply_to.media_type === 'image' ? '📷 Image' :
                             msg.reply_to.media_type === 'video' ? '🎬 Vidéo' :
                             msg.reply_to.media_type === 'pdf' ? '📄 Document PDF' :
                             msg.reply_to.media_type === 'location' ? '📍 Localisation' :
                             (msg.reply_to.content || '')}
                          </p>
                        </div>
                      )}

                      {/* Gift message */}
                      {msg.media_type === 'gift' && msg.gift_id && (
                        <div className="flex flex-col items-center gap-1 py-2 px-3" data-testid={`gift-msg-${msg.id}`}>
                          <span className="text-6xl drop-shadow-lg animate-bounce" style={{ animationDuration: '2s', animationIterationCount: 'infinite' }}>
                            {/* The emoji is part of the catalog; lookup happens via inline map below */}
                            <GiftEmojiInline giftId={msg.gift_id} />
                          </span>
                          <span className="text-[11px] text-white/80 font-medium">Cadeau envoyé</span>
                        </div>
                      )}

                      {/* Media content */}
                      {msg.media_url && msg.media_type === 'image' && (
                        <MediaImage 
                          src={getMediaUrl(msg.media_url)} 
                          isOwn={isOwn}
                          onOpen={(s) => setLightbox({ type: 'image', src: s })}
                        />
                      )}
                      
                      {msg.media_url && msg.media_type === 'video' && (
                        <MediaVideo 
                          src={getMediaUrl(msg.media_url)} 
                          isOwn={isOwn}
                          onOpen={(s) => setLightbox({ type: 'video', src: s })}
                        />
                      )}
                      
                      {msg.media_url && msg.media_type === 'voice' && (
                        <>
                          <VoiceMessagePlayer 
                            src={getMediaUrl(msg.media_url)} 
                            isOwn={isOwn}
                            autoPlay={msg.is_voice_sticker && autoPlayMessageIdsRef.current.has(msg.id)}
                          />
                          {!msg.is_voice_sticker && (
                            <button
                              type="button"
                              onClick={() => handleTranslateVoice(msg)}
                              disabled={translatingMsgId === msg.id}
                              className={`mb-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                                isOwn ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300'
                              } disabled:opacity-50`}
                              data-testid={`translate-voice-${msg.id}`}
                              title="Traduire ce vocal"
                            >
                              <Languages className="w-3 h-3" />
                              {translatingMsgId === msg.id ? '…' : 'Traduire'}
                            </button>
                          )}
                        </>
                      )}

                      {/* PDF document — preview card with download button */}
                      {msg.media_url && msg.media_type === 'pdf' && (
                        <PdfMessageCard message={msg} isOwn={isOwn} />
                      )}
                      
                      {/* Location content */}
                      {msg.media_type === 'location' && msg.location_lat && msg.location_lng && (
                        <LocationMap
                          lat={msg.location_lat}
                          lng={msg.location_lng}
                          expiresAt={msg.location_expires_at}
                          isOwn={isOwn}
                        />
                      )}
                      
                      {/* Text content - hide empty for location */}
                      {msg.content && msg.media_type !== 'location' && (
                        <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                          {renderMentions(msg.content, (u) => navigate(`/profile/u/${u}`))}
                        </p>
                      )}
                      
                      {/* Timestamp + read receipt - location messages have their own time display */}
                      {msg.media_type !== 'location' && (
                        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          {msg.ai_agent && (
                            <span className="text-[9px] px-1.5 rounded-full bg-fuchsia-500/30 text-fuchsia-100 font-semibold" data-testid="msg-ai-agent-badge">IA</span>
                          )}
                          <span className="text-[10px] opacity-60">
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {isOwn && (
                            <span className="text-[10px] opacity-80" data-testid={msg.is_read ? 'msg-read' : 'msg-sent'}>
                              {msg.is_read ? (
                                <span className="flex items-center gap-0.5">
                                  <Check className="w-3 h-3 -mr-1.5" />
                                  <Check className="w-3 h-3" />
                                </span>
                              ) : (
                                <Check className="w-3 h-3 opacity-60" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Existing reactions on the message */}
                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div className={`mt-1 flex gap-1 flex-wrap ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`reactions-${msg.id}`}>
                        {Object.entries(msg.reactions).map(([em, users]) => {
                          const youReacted = Array.isArray(users) && users.includes(user.id);
                          return users && users.length > 0 ? (
                            <button
                              key={em}
                              onClick={(e) => { e.stopPropagation(); reactToMessage(msg, em); }}
                              className={`px-1.5 py-0.5 rounded-full text-xs flex items-center gap-1 transition-colors ${
                                youReacted ? 'bg-magenta-500/30 border border-magenta-400/60' : 'bg-white/10 border border-white/10 hover:bg-white/20'
                              }`}
                            >
                              <span>{em}</span>
                              <span className="text-[10px] text-white/80">{users.length}</span>
                            </button>
                          ) : null;
                        })}
                      </div>
                    )}
                    {/* Reaction picker (long-press to open) */}
                    {reactionPickerFor === msg.id && (
                      <div className={`mt-1 flex gap-1 px-2 py-1.5 rounded-full bg-gray-900/95 border border-white/20 shadow-2xl ${isOwn ? 'self-end' : 'self-start'}`} data-testid={`reaction-picker-${msg.id}`} onClick={(e) => e.stopPropagation()}>
                        {['❤️', '👍', '😂', '😮', '😢', '🔥'].map(em => (
                          <button
                            key={em}
                            onClick={(e) => { e.stopPropagation(); reactToMessage(msg, em); }}
                            className="text-2xl hover:scale-125 transition-transform"
                            data-testid={`react-${em}-${msg.id}`}
                          >{em}</button>
                        ))}
                        <button
                          onClick={(e) => { e.stopPropagation(); setReactionPickerFor(null); }}
                          className="ml-1 text-white/60 hover:text-white text-sm self-center"
                          aria-label="Close"
                        >×</button>
                      </div>
                    )}
                  </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          {/* Typing indicator (1-to-1 conversations only) */}
          {!activeConversation.is_group && !activeConversation.isNew && typingUsers.length > 0 && (
            <div className="flex justify-start mt-2" data-testid="typing-indicator">
              <div className="px-3.5 py-2 bg-white/10 text-white rounded-2xl rounded-bl-md shadow-md flex items-center gap-2">
                <span className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span className="text-[11px] text-white/80">{(typingUsers[0]?.user?.name || activeConversation.other_user?.name || '')} écrit…</span>
              </div>
            </div>
          )}
          {activeConversation.isNew && (
            <div className="text-center text-gray-400 py-4">
              <p className="mb-2">Envoyez votre premier message comme invitation</p>
              <p className="text-sm">L&apos;utilisateur devra accepter votre invitation pour continuer</p>
            </div>
          )}
          <div ref={messagesEndRef} />
          {/* Extra space for input field and keyboard */}
          <div style={{ height: keyboardHeight > 0 ? keyboardHeight + 120 : 160 }} />
        </div>

        {/* Input - Adapts to keyboard height on iOS */}
        <div 
          className="fixed left-0 right-0 bg-black/95 backdrop-blur-md border-t border-white/10 p-4 z-50 transition-all duration-200"
          style={{ 
            bottom: keyboardHeight > 0 ? keyboardHeight : 64 // 64px = Navigation bar height
          }}
        >
          {/* Media options popup */}
          {showMediaOptions && (
            <div className="absolute bottom-full left-4 mb-2 bg-gray-900 border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*,video/*,application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    let mediaType = 'image';
                    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) mediaType = 'pdf';
                    else if (file.type.startsWith('video/')) mediaType = 'video';
                    handleFileSelect(e, mediaType);
                  }
                }}
                className="hidden"
              />
              <button
                onClick={() => {
                  fileInputRef.current.accept = 'image/*';
                  fileInputRef.current.click();
                }}
                className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center space-x-3"
              >
                <Image className="w-5 h-5 text-green-400" />
                <span>Photo</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current.accept = 'video/*';
                  fileInputRef.current.click();
                }}
                className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center space-x-3"
              >
                <Video className="w-5 h-5 text-blue-400" />
                <span>Vidéo (max 2 min)</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current.accept = 'application/pdf,.pdf';
                  fileInputRef.current.click();
                }}
                className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center space-x-3"
                data-testid="open-pdf-picker-btn"
              >
                <FileText className="w-5 h-5 text-orange-400" />
                <span>Document PDF (max 30 Mo)</span>
              </button>
              <button
                onClick={() => { setShowMediaOptions(false); setShowStickerPicker(true); }}
                className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center space-x-3 border-t border-white/10"
                data-testid="open-sticker-picker-btn"
              >
                <Smile className="w-5 h-5 text-yellow-400" />
                <span>Stickers vocaux</span>
              </button>
              <button
                onClick={() => { setShowMediaOptions(false); setShowGiftPicker(true); }}
                disabled={activeConversation?.isNew}
                className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center space-x-3 border-t border-white/10 disabled:opacity-40"
                data-testid="open-gift-picker-btn"
              >
                <Gift className="w-5 h-5 text-fuchsia-300" />
                <span>Cadeau culturel</span>
              </button>
            </div>
          )}
          
          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-red-500/20 border border-red-500/50 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-red-400">Enregistrement en cours... {recordingTime}s / 120s</span>
              </div>
              <button
                onClick={stopRecording}
                className="p-2 bg-red-500 rounded-full"
              >
                <Square className="w-4 h-4" fill="white" />
              </button>
            </div>
          )}

          {/* Voice preview before sending (WhatsApp-style) */}
          {pendingVoice && !isRecording && (
            <div className="absolute bottom-full left-4 right-4 mb-2">
              <VoicePreviewBar
                url={pendingVoice.url}
                sending={sending}
                onCancel={() => { URL.revokeObjectURL(pendingVoice.url); setPendingVoice(null); }}
                onSend={async () => {
                  const pv = pendingVoice;
                  await uploadMedia(pv.file, 'voice');
                  URL.revokeObjectURL(pv.url);
                  setPendingVoice(null);
                }}
              />
            </div>
          )}
          
          <div className="flex items-center gap-1.5 max-w-4xl mx-auto">
            {/* Reply preview banner (above input) */}
            {replyToMessage && (
              <div className="absolute bottom-full left-3 right-3 mb-2 bg-gray-900/95 border border-magenta-500/40 rounded-xl px-3 py-2 flex items-start gap-2" data-testid="reply-preview">
                <CornerUpLeft className="w-4 h-4 text-magenta-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-magenta-400 font-semibold">
                    Réponse à {replyToMessage.sender_id === user.id ? 'vous-même' : (activeConversation.other_user?.name || '...')}
                  </p>
                  <p className="text-xs text-gray-300 truncate">
                    {replyToMessage.media_type === 'voice' ? '🎙️ Message vocal' :
                     replyToMessage.media_type === 'image' ? '📷 Image' :
                     replyToMessage.media_type === 'video' ? '🎬 Vidéo' :
                     replyToMessage.media_type === 'pdf' ? '📄 Document PDF' :
                     replyToMessage.media_type === 'location' ? '📍 Localisation' :
                     (replyToMessage.content || '')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyToMessage(null)}
                  className="p-1 hover:bg-white/10 rounded-full"
                  data-testid="cancel-reply-btn"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            )}

            {/* Media button */}
            <button
              type="button"
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              className="p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              disabled={sending || isRecording}
              data-testid="media-options-btn"
            >
              <Image className="w-4 h-4" />
            </button>

            {/* Voice recording button */}
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              className={`p-2.5 rounded-full transition-colors flex-shrink-0 ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
              disabled={sending}
              data-testid="voice-record-btn"
            >
              <Mic className="w-4 h-4" />
            </button>
            
            {/* Location sharing button - mobile only */}
            {!activeConversation.isNew && Capacitor.isNativePlatform() && (
              <button
                type="button"
                onClick={handleShareLocation}
                className={`p-2.5 rounded-full transition-colors flex-shrink-0 ${
                  sendingLocation 
                    ? 'bg-green-500/30' 
                    : 'bg-white/10 hover:bg-green-500/20'
                }`}
                disabled={sending || isRecording || sendingLocation}
                data-testid="share-location-btn"
                title="Partager ma position"
              >
                {sendingLocation ? (
                  <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <MapPin className="w-4 h-4 text-green-500" />
                )}
              </button>
            )}
            
            {/* Text input — auto-grow textarea (WhatsApp-style wrap, up to 5 lines) */}
            <form onSubmit={handleSendMessage} className="flex-1 flex items-end gap-1.5 min-w-0">
              <textarea
                ref={inputRef}
                rows={1}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value.slice(0, 3500));
                  // auto-grow
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                maxLength={3500}
                placeholder="Message…"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                className="flex-1 min-w-0 px-4 py-2.5 bg-white/10 border border-white/10 rounded-3xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500 resize-none leading-snug"
                disabled={sending || isRecording}
                data-testid="message-input"
                onFocus={() => {
                  setShowMediaOptions(false);
                  setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }, 300);
                }}
              />
              {newMessage.length > 2800 && (
                <span className={`text-[10px] font-mono flex-shrink-0 ${newMessage.length >= 3500 ? 'text-red-400' : 'text-amber-300'}`} data-testid="message-charcount">
                  {newMessage.length}/3500
                </span>
              )}
              <button
                type="submit"
                disabled={sending || !newMessage.trim() || isRecording}
                className="p-2.5 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                data-testid="send-message-btn"
                aria-label="Envoyer"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
        
        {/* Report Modal */}
        {reportingMessage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Signaler ce message</h3>
                <button
                  onClick={() => {
                    setReportingMessage(null);
                    setReportReason('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Pourquoi signalez-vous ce message ?"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
                rows={3}
              />
              
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={() => {
                    setReportingMessage(null);
                    setReportReason('');
                  }}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleReportMessage}
                  disabled={!reportReason.trim()}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-700 rounded-xl transition-colors"
                >
                  Signaler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phone Call Modal */}
        {showPhoneModal && (otherUserPhone || otherUserEsimNumbers.length > 0) && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
            <div className="bg-gray-900 rounded-2xl p-6 max-w-sm w-full border border-white/10 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-4">
                {activeConversation?.other_user?.name}
              </h3>

              {otherUserPhone && (
                <div className="mb-3 p-3 bg-white/5 rounded-xl border border-white/10">
                  <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-1">
                    📱 Mobile
                  </div>
                  <p className="text-xl font-bold text-green-400" data-testid="phone-number-display">
                    {otherUserPhone}
                  </p>
                  <a
                    href={`tel:${otherUserPhone}`}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm font-semibold"
                    data-testid="initiate-call-btn"
                  >
                    <Phone className="w-4 h-4" />
                    Appeler
                  </a>
                </div>
              )}

              {otherUserEsimNumbers.map((e, idx) => (
                <div key={e.msisdn} className="mb-3 p-3 bg-white/5 rounded-xl border border-purple-400/30" data-testid={`esim-number-${idx}`}>
                  <div className="text-[10px] uppercase tracking-wider text-purple-300 font-bold mb-1 flex items-center justify-center gap-1">
                    🌍 eSIM {e.country_title ? `· ${e.country_title}` : ''}
                  </div>
                  <p className="text-xl font-bold text-purple-300">
                    {e.msisdn}
                  </p>
                  <a
                    href={`tel:${e.msisdn}`}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm font-semibold"
                    data-testid={`esim-call-btn-${idx}`}
                  >
                    <Phone className="w-4 h-4" />
                    Appeler
                  </a>
                </div>
              ))}

              <button
                onClick={() => {
                  setShowPhoneModal(false);
                  setOtherUserPhone(null);
                  setOtherUserEsimNumbers([]);
                }}
                className="w-full mt-2 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Voice Sticker Picker */}
        {showStickerPicker && (
          <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
            <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-white/10" data-testid="sticker-picker">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Smile className="w-5 h-5 text-yellow-400" />
                  Stickers vocaux
                </h3>
                <button onClick={() => setShowStickerPicker(false)} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="close-sticker-picker">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">Tapez pour envoyer un emoji audio</p>
              <div className="grid grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                {stickers.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleSendSticker(s.id)}
                    disabled={sendingSticker === s.id}
                    data-testid={`sticker-${s.id}`}
                    className="p-3 rounded-2xl bg-white/5 hover:bg-yellow-500/20 border border-white/10 hover:border-yellow-500/40 transition-all flex flex-col items-center gap-1 disabled:opacity-50"
                  >
                    <span className="text-3xl leading-none">{s.emoji}</span>
                    <span className="text-xs text-gray-200">{s.label}</span>
                    {sendingSticker === s.id && (
                      <span className="text-[10px] text-magenta-400 animate-pulse">envoi…</span>
                    )}
                  </button>
                ))}
                {stickers.length === 0 && (
                  <div className="col-span-3 text-center text-gray-500 py-8">Chargement…</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Voice translation language picker */}
        {translateTargetMsg && (
          <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" onClick={() => setTranslateTargetMsg(null)}>
            <div className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-white/10" onClick={(e) => e.stopPropagation()} data-testid="translate-lang-picker">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Languages className="w-5 h-5 text-cyan-400" />
                  Traduire en…
                </h3>
                <button onClick={() => setTranslateTargetMsg(null)} className="p-1.5 hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {SUPPORTED_TRANSLATIONS.map(l => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => handleTranslateVoice(translateTargetMsg, l.code)}
                    className="px-3 py-3 bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/40 rounded-xl text-sm font-medium transition-all"
                    data-testid={`translate-target-${l.code}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Voice translation result modal */}
        {translationResult && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" data-testid="translation-modal">
            <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Languages className="w-5 h-5 text-cyan-400" />
                  Traduction vocale
                </h3>
                <button onClick={() => setTranslationResult(null)} className="p-1.5 hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Original</p>
                  <p className="text-sm bg-white/5 p-3 rounded-lg">{translationResult.original_text}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Traduction ({translationResult.target_lang})</p>
                  <p className="text-sm bg-cyan-500/10 border border-cyan-500/30 p-3 rounded-lg">{translationResult.translated_text}</p>
                </div>
                {translationResult.audio_base64 && (
                  <audio
                    src={`data:audio/mpeg;base64,${translationResult.audio_base64}`}
                    controls
                    autoPlay
                    className="w-full"
                    data-testid="translation-audio"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hide bottom navigation while keyboard is open in chat view — mirrors
            the behavior of WhatsApp / Telegram / Instagram so the user gets full
            screen space for the conversation while typing. */}
        <Navigation />

        {/* Agora Voice Call modal — also rendered in chat view */}
        {showVoiceCall && activeConversation?.other_user && (
          <VoiceCallModal
            peerUserId={activeConversation.other_user.id}
            peerName={activeConversation.other_user.name || activeConversation.other_user.username}
            peerAvatarUrl={activeConversation.other_user.profile_image_url}
            callType={callType}
            onClose={() => setShowVoiceCall(false)}
          />
        )}

        {/* Full-screen viewer for chat images & videos — MUST live inside the
            chat-view branch too, otherwise tapping a thumbnail does nothing
            until the user navigates back to the conversations list. */}
        <MediaLightbox
          open={!!lightbox}
          type={lightbox?.type}
          src={lightbox?.src}
          onClose={() => setLightbox(null)}
        />
        <GiftPicker
          open={showGiftPicker}
          onClose={() => setShowGiftPicker(false)}
          onSend={sendGift}
        />
      </div>
    );
  }

  // Main view (conversations and invitations)
  return (
    <div className={`min-h-screen text-white ${inGroupChat ? '' : 'pb-20'} ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      {/* Header (hidden during group chat for full-screen UX, like WhatsApp) */}
      {!inGroupChat && (
      <div className={`sticky top-0 z-40 backdrop-blur-md border-b border-white/10 p-4 space-y-4 ${isDark ? 'bg-black/90' : 'bg-white/10'}`}>
        <h1 className="text-2xl font-bold">{t('messages.title')}</h1>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t("messages.searchPlaceholder")}
            className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/10 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
          />
        </div>
        
        {/* Search Results */}
        {searchQuery && (
          <div className="absolute left-0 right-0 mx-4 mt-2 bg-gray-900 border border-white/10 rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
            {searching ? (
              <div className="p-4 text-center text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-magenta-500 mx-auto"></div>
              </div>
            ) : !Array.isArray(searchResults) || searchResults.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                Aucun utilisateur trouvé
              </div>
            ) : (
              searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    navigate(`/messages/new/${result.id}`);
                  }}
                  className="w-full p-3 hover:bg-white/10 transition-colors text-left flex items-center space-x-3"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full flex items-center justify-center text-white font-bold overflow-hidden shrink-0">
                    {result.profile_image_url ? (
                      <img
                        src={getMediaUrl(result.profile_image_url)}
                        alt={result.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      result.name?.[0]?.toUpperCase()
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">{result.name}</p>
                    <p className="text-sm text-gray-400">@{result.username}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      )}

      {/* Tabs (hidden during group chat) */}
      {!inGroupChat && (
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setView('conversations')}
          className={`flex-1 py-4 font-semibold transition-colors ${
            view === 'conversations'
              ? 'text-magenta-500 border-b-2 border-magenta-500'
              : 'text-gray-400'
          }`}
        >
          <span>{t('messages.conversations')} ({conversations.length})</span>
        </button>
        <button
          onClick={() => setView('groups')}
          className={`flex-1 py-4 font-semibold transition-colors ${
            view === 'groups'
              ? 'text-violet-400 border-b-2 border-violet-400'
              : 'text-gray-400'
          }`}
          data-testid="groups-tab-btn"
        >
          <span>{t('groups.tab') || 'Groupes'}</span>
        </button>
        <button
          onClick={() => setView('invitations')}
          className={`flex-1 py-4 font-semibold transition-colors ${
            view === 'invitations'
              ? 'text-magenta-500 border-b-2 border-magenta-500'
              : 'text-gray-400'
          }`}
        >
          <span>{t('messages.invitations')} ({invitations.length + groupInvitations.length})</span>
        </button>
      </div>
      )}

      {/* Content */}
      <div className={inGroupChat ? '' : 'p-4'}>
        {view === 'conversations' && (
          <div className="space-y-2">
            {!Array.isArray(conversations) || conversations.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune conversation</p>
                <p className="text-sm mt-2">Visitez un profil et envoyez un message</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const hasUnread = conv.unread_count > 0;
                return (
                  <button
                    key={conv.conversation_id}
                    onClick={() => {
                      setActiveConversation(conv);
                      loadConversation(conv.conversation_id);
                    }}
                    data-testid={`conversation-${conv.conversation_id}`}
                    className={`w-full p-4 rounded-xl transition-colors text-left ${
                      hasUnread 
                        ? 'bg-magenta-500/10 border border-magenta-500/30 hover:bg-magenta-500/20' 
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Profile picture with online indicator */}
                      <div className="relative">
                        <div
                          role={conv.other_user?.has_active_story ? 'button' : undefined}
                          onClick={conv.other_user?.has_active_story ? (e) => { e.stopPropagation(); setStoryViewerUserId(conv.other_user.id); } : undefined}
                          className={`w-12 h-12 rounded-full p-[2px] ${conv.other_user?.has_active_story ? 'bg-gradient-to-tr from-yellow-400 via-fuchsia-500 to-purple-600 cursor-pointer' : 'bg-gradient-to-r from-magenta-500 to-violet-600'}`}
                          data-testid={conv.other_user?.has_active_story ? `conv-story-ring-${conv.conversation_id}` : undefined}
                        >
                          <div className={`w-full h-full rounded-full flex items-center justify-center text-white font-bold overflow-hidden ${conv.other_user?.has_active_story ? 'bg-gray-900 border-2 border-gray-900' : ''}`}>
                          {conv.other_user?.profile_image_url ? (
                            <img 
                              src={getMediaUrl(conv.other_user.profile_image_url)} 
                              alt={conv.other_user?.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            conv.other_user?.name?.[0]?.toUpperCase()
                          )}
                          </div>
                        </div>
                        {/* Online indicator */}
                        {conv.other_user?.is_online && (
                          <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-gray-900"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold flex items-center gap-1 ${hasUnread ? 'text-white' : ''}`}>
                            {conv.other_user?.name}
                            {conv.other_user?.is_verified && <VerifiedBadge size="xs" />}
                          </h3>
                          {hasUnread && (
                            <span className="px-2 py-0.5 bg-magenta-500 text-white text-xs font-bold rounded-full">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm truncate ${hasUnread ? 'text-white/80 font-medium' : 'text-gray-400'}`}>
                          {conv.last_message?.content || t('messages.noMessages')}
                        </p>
                      </div>
                      <div className="text-right">
                        {conv.last_message && (
                          <p className={`text-xs ${hasUnread ? 'text-magenta-400' : 'text-gray-500'}`}>
                            {new Date(conv.last_message.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {view === 'groups' && (
          <GroupsPanel currentUserId={user.id} onActiveGroupChange={setInGroupChat} />
        )}

        {view === 'invitations' && (
          <div className="space-y-4">
            {!Array.isArray(invitations) || invitations.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune invitation en attente</p>
              </div>
            ) : (
              invitations.map((inv) => (
                <div
                  key={inv.conversation_id}
                  className="p-4 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full flex items-center justify-center text-white font-bold">
                        {inv.sender?.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold">{inv.sender?.name}</h3>
                        <p className="text-sm text-gray-400">@{inv.sender?.username}</p>
                      </div>
                    </div>
                  </div>
                  
                  {inv.first_message && (
                    <div className="mb-4 p-3 bg-white/5 rounded-lg">
                      <p className="text-sm text-gray-300">{inv.first_message.content}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleAcceptInvitation(inv.conversation_id)}
                      className="flex-1 py-2 bg-green-500 hover:bg-green-600 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      <Check className="w-4 h-4" />
                      <span>Accepter</span>
                    </button>
                    <button
                      onClick={() => handleRejectInvitation(inv.conversation_id)}
                      className="flex-1 py-2 bg-red-500 hover:bg-red-600 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Refuser</span>
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Group join requests */}
            {groupInvitations.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="text-sm uppercase tracking-wide text-violet-300 font-semibold">
                  {t('groups.invitations') || 'Demandes de groupe'} ({groupInvitations.length})
                </h3>
                {groupInvitations.map(req => (
                  <div key={req.id} className="bg-white/5 border border-violet-500/30 rounded-xl p-4" data-testid={`group-invitation-${req.id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-violet-700/30 overflow-hidden flex items-center justify-center">
                        {req.from_user?.profile_image_url ? (
                          <img src={getMediaUrl(req.from_user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-6 h-6 text-violet-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{req.from_user?.name || req.from_user?.username}</p>
                        <p className="text-xs text-gray-400 truncate">
                          → <span className="text-violet-300">{req.group?.name}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleGroupInvitation(req, true)}
                        className="flex-1 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg font-semibold flex items-center justify-center gap-2"
                        data-testid={`accept-group-${req.id}`}
                      >
                        <Check className="w-4 h-4" /> {t('groups.accept') || 'Accepter'}
                      </button>
                      <button
                        onClick={() => handleGroupInvitation(req, false)}
                        className="flex-1 py-2 bg-white/10 hover:bg-red-500/30 text-red-300 rounded-lg font-semibold flex items-center justify-center gap-2"
                        data-testid={`reject-group-${req.id}`}
                      >
                        <X className="w-4 h-4" /> {t('groups.reject') || 'Refuser'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hide bottom nav while keyboard is open — applies to all message views
          including Groups (so the send button doesn't conflict with the bar) */}
      {!inGroupChat && <Navigation />}

      {/* v50.10.8 — story ring in conversations list → open peer's story */}
      {storyViewerUserId && <StoryViewer userId={storyViewerUserId} onClose={() => setStoryViewerUserId(null)} />}

      {/* Full-screen viewer for chat images & videos */}
      <MediaLightbox
        open={!!lightbox}
        type={lightbox?.type}
        src={lightbox?.src}
        onClose={() => setLightbox(null)}
      />

      {/* Agora Voice Call modal */}
      {showVoiceCall && activeConversation?.other_user && (
        <VoiceCallModal
          peerUserId={activeConversation.other_user.id}
          peerName={activeConversation.other_user.name || activeConversation.other_user.username}
          peerAvatarUrl={activeConversation.other_user.profile_image_url}
          callType={callType}
          onClose={() => setShowVoiceCall(false)}
        />
      )}
    </div>
  );
};

// ===== PDF message card — preview tile with name, size, and download button =====
// Receivers see a clean file card (rather than a noisy raw link). Tapping the
// download button triggers a streamed `fetch + blob` so the file lands in the
// user's Downloads folder with the original filename — including on Capacitor
// (where <a download> attribute is unreliable on iOS).
const PdfMessageCard = ({ message, isOwn }) => {
  const [busy, setBusy] = React.useState(false);
  const name = message.media_filename || 'document.pdf';
  const sizeKb = message.media_size ? Math.round(message.media_size / 1024) : null;
  const url = getMediaUrl(message.media_url);

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      toast.error("Échec du téléchargement");
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`mt-1 mb-1 flex items-center gap-3 p-2.5 rounded-2xl max-w-[260px] ${
        isOwn ? 'bg-white/15' : 'bg-black/20'
      }`}
      data-testid={`pdf-card-${message.id}`}
    >
      <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-400/40 flex items-center justify-center shrink-0">
        <FileText className="w-5 h-5 text-orange-300" />
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block text-sm font-semibold truncate hover:underline"
          data-testid={`pdf-open-${message.id}`}
          title={name}
        >
          {name}
        </a>
        <p className="text-[10px] text-gray-300 opacity-80">
          PDF{sizeKb ? ` · ${sizeKb < 1024 ? `${sizeKb} Ko` : `${(sizeKb / 1024).toFixed(1)} Mo`}` : ''}
        </p>
      </div>
      <button
        onClick={download}
        disabled={busy}
        className="p-2 rounded-full bg-orange-500/30 hover:bg-orange-500/50 disabled:opacity-50 shrink-0"
        data-testid={`pdf-download-${message.id}`}
        title="Télécharger"
      >
        <Download className="w-4 h-4 text-orange-100" />
      </button>
    </div>
  );
};

export default Messages;
